import { inject, Injectable } from '@angular/core';
import { Subscription, Subject } from 'rxjs';

import { LeaderboardHttpService } from '../http/http.service';
import { NonNullableFormBuilder } from '@angular/forms';
import { Pagination } from '@app/core/model/pagination.model';
import { UserSimpleGamification, UserSimpleGoal } from '../../model/user.model';
import { UserService } from '@app/user';

import { BroadcastService, BroadcastMessage } from '@app/core/service';

type LeaderboardType = 'gamification' | 'goal';

@Injectable()
export class LeaderboardLazyService {
    private readonly leaderboardHttpService = inject(LeaderboardHttpService);
    private readonly userService = inject(UserService);
    private readonly broadcastService = inject(BroadcastService);
    private readonly fb = inject(NonNullableFormBuilder);

    /**
     * Loading Flag
     */
    loading = false;

    /**
     * Leaderboard
     */
    _leaderboard: {
        gamification: Record<string, Record<string, UserSimpleGamification[]>>;
        goal: Record<string, Record<string, UserSimpleGoal[]>>;
    } = {
        gamification: {},
        goal: {},
    };

    /**
     * Total Items
     */
    _total: {
        gamification: Record<string, number | null>;
        goal: Record<string, number | null>;
    } = {
        gamification: {},
        goal: {},
    };

    /**
     * Total Items
     */
    _offsets: {
        gamification: Record<string, number>;
        goal: Record<string, number>;
    } = {
        gamification: {},
        goal: {},
    };

    /**
     * Ranges
     */
    readonly ranges = <const>['week', 'month', 'yeartodate', 'alltime'];

    /**
     * Types
     */
    types = ['goal'];

    /**
     * Range FormControl
     */
    readonly form = this.fb.group({
        type: this.fb.control(this.types[0]),
        range: this.fb.control<LeaderboardLazyService['ranges'][number]>(this.ranges[0]),
    });

    /**
     * Pagination
     */
    _pagination: Pagination = {
        offset: 0,
        limit: 20,
    };

    /**
     * viewed Leaderboard
     */
    viewedPart: (UserSimpleGamification | UserSimpleGoal)[] | null = null;

    /**
     * Subject
     */
    private updatesEvent = new Subject();

    /**
     * Subscriptions
     */
    private subscriptions = new Subscription();

    init(): void {
        // gamification
        if (this.userService.access.includes('gamification')) {
            this.types.unshift('gamification');
        }
        this.initForm();
        this.subscriptions.add(this.broadcastService.broadcasts.subscribe((broadcast) => this.broadcasts(broadcast)));
    }

    destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Subject
     */
    get updates() {
        return this.updatesEvent.asObservable();
    }

    /**
     * Get Leaderboard
     */
    get leaderboard() {
        // create empty tree
        this.createEmptyTree();
        const { type, range } = this.form.getRawValue();
        const leaderboardType = <LeaderboardType>type;
        // check if loaded
        if (this._leaderboard[leaderboardType][range]['items']) {
            this.viewedPart = this._leaderboard[leaderboardType][range]['items'];
        } else {
            this.loadList();
        }
        // return viewed part
        return this.viewedPart;
    }

    getLeaderBoard() {
        const { type, range } = this.form.getRawValue();
        const leaderboardType = <LeaderboardType>type;
        if (this._leaderboard[leaderboardType][range]['items'].length < (this._total[leaderboardType][range] ?? 0)) {
            this._offsets[leaderboardType][range] = this._offsets[leaderboardType][range] + 20;
            this.loadList();
        }
    }

    /**
     * Get Total
     */
    get total() {
        const { type, range } = this.form.getRawValue();
        const leaderboardType = <LeaderboardType>type;
        return this._total[leaderboardType][range] ? this._total[leaderboardType][range] : 0;
    }

    /**
     * Set Pagination
     */
    set pagination(pagination: Pagination) {
        this._pagination = pagination;
    }

    /**
     * Get Pagination
     */
    get pagination() {
        return this._pagination;
    }

    /**
     * Load Leaderboard
     */
    loadList(): void {
        if (this.loading) {
            return;
        }

        const options = this.form.getRawValue();
        const leaderboardType = <LeaderboardType>options.type;

        this.loading = true;
        this.updatesEvent.next(true);

        switch (options.type) {
            case 'gamification':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGamification(options.range, this.pagination.limit, this._offsets[leaderboardType][options.range])
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range)),
                );
                break;
            case 'goal':
                this.subscriptions.add(
                    this.leaderboardHttpService
                        .getLeaderboardGoal(options.range, this.pagination.limit, this._offsets[leaderboardType][options.range])
                        .subscribe((data) => this.handleResponse(data.users, data.total, options.type, options.range)),
                );
                break;
        }
    }

    /**
     * Reset Pagination and Form
     */
    reset(): void {
        this.form.patchValue(
            {
                type: this.types[0],
                range: this.ranges[0],
            },
            { emitEvent: false },
        );
        this.pagination = {
            offset: 0,
            limit: 20,
        };
        this.viewedPart = null;
    }

    /**
     * Response Handler
     * @param users List of Users
     * @param total Total number of Users
     * @param type gamification or goal
     * @param range week, month or all
     * @param offset offset of the query
     */
    private handleResponse(users: (UserSimpleGamification | UserSimpleGoal)[], total: number, type: string, range: string): void {
        const leaderboardType = <LeaderboardType>type;
        if (!this._leaderboard[leaderboardType][range]['items']) {
            this._leaderboard[leaderboardType][range]['items'] = <any>users;
        } else {
            this._leaderboard[leaderboardType][range]['items'] = <any>[...this._leaderboard[leaderboardType][range]['items'], ...users];
        }

        this._total[leaderboardType][range] = total;
        this.loading = false;
        this.updatesEvent.next(true);
    }

    /**
     * Init Form
     */
    private initForm(): void {
        // subscription
        this.subscriptions.add(
            this.form.valueChanges.subscribe(() => {
                this.pagination = {
                    offset: 0,
                    limit: 20,
                };
                this.viewedPart = null;
                this.updatesEvent.next(true);
            }),
        );
    }

    /**
     * Create Empty tree
     */
    private createEmptyTree(): void {
        const { type, range } = this.form.getRawValue();
        const leaderboardType = <LeaderboardType>type;
        // type
        if (!this._leaderboard[leaderboardType]) {
            this._leaderboard[leaderboardType] = {};
            this._total[leaderboardType] = {};
            this._offsets[leaderboardType] = {};
        }
        // range
        if (!this._leaderboard[leaderboardType][range]) {
            this._leaderboard[leaderboardType][range] = {};
            this._total[leaderboardType][range] = null;
            this._offsets[leaderboardType][range] = 0;
        }
    }

    /**
     * Broadcast
     */
    private broadcasts(broadcast: BroadcastMessage): void {
        switch (broadcast.message) {
            case 'episode-completed':
            case 'discussion-topic-created':
            case 'discussion-topic-reply-created':
            case 'discussion-reply-reply-created':
            case 'user-self-edited':
                this.reset();
                break;
        }
    }
}
